import React from 'react';
import styled from 'styled-components';

import Input from '../../../components/Input';
import { isValid } from '../../../utils';
import { useNavigate } from 'react-router-dom';
import { useAppHandlers } from '../../../services/actions/app/useAppHandlers';
import { useAuthRequest } from '../../../services/apis/auth/useAuthRequest';
import { AppRoutes } from '../../../interfaces/store/appStore';
import { styles } from '../../../styles/themes/style';
import Button from '../../../components/Button';
import { AuthMethodButton, FullLogo, AuthInputWrap } from '../authFormCommon';
import AuthLegalFooter from '../components/AuthLegalFooter';


const ForgotPasswordSection = React.forwardRef((props: any, ref: any) => {
    const [step, setStep] = React.useState(1);
    const [values, setValues] = React.useState<any>({});
    const [valid, setValid] = React.useState(false);
    const [loaded, setLoaded] = React.useState(true);
    const [loading, setLoading] = React.useState(false);
    const [showPassword, setShowPassword] = React.useState<any>(false);
    const [verificationDigits, setVerificationDigits] = React.useState<string[]>(Array(8).fill(''));
    const verificationInputRefs = React.useRef<Array<HTMLInputElement | null>>([]);
    const { showSuccessToast, showErrorToast } = useAppHandlers();
    const { verificationCode, verificationCodeValidate, resetPassword } = useAuthRequest();
    const navigate = useNavigate();

    /* useEffect(() => {
        resetAuthStore()
    }, []) */

    const SendResetInstructions = React.useCallback(async () => {
        setLoading(true);

        try {
            if (valid) {
                const response = await verificationCode({
                    email: values.email
                });

                if (response?.success) {
                    setVerificationDigits(Array(8).fill(''));
                    updateValue('verificationCode', '');
                    setStep(2);

                    showSuccessToast({ message: `Your verification code has been successfully sent.` });
                }
                else {
                    showErrorToast({ message: response?.message });
                }
            }
        }
        catch (error) {
            console.log('Sign up with email', error);
        }

        setLoading(false);
    }, [values, valid]);

    const VerificationCodeConfirm = React.useCallback(async () => {
        setLoading(true);

        try {
            if (valid) {
                const response = await verificationCodeValidate({
                    email: values.email,
                    code: values.verificationCode
                });

                if (response?.success) {
                    setStep(3);

                    updateValue('token', response.data.token);

                    showSuccessToast({ message: `Your verification code has been confirmed successfully.` });
                }
                else {
                    showErrorToast({ message: response?.message });
                }
            }
        }
        catch (error) {
            console.log('Sign up with email', error);
        }

        setLoading(false);
    }, [values, valid]);

    const resendVerificationCode = React.useCallback(async () => {
        if (!values.email) return;
        setLoading(true);
        try {
            const response = await verificationCode({
                email: values.email,
            });
            if (response?.success) {
                showSuccessToast({ message: 'A new verification code has been sent.' });
            } else {
                showErrorToast({ message: response?.message });
            }
        } catch (error) {
            console.log('Resend verification code', error);
        } finally {
            setLoading(false);
        }
    }, [values.email, verificationCode, showSuccessToast, showErrorToast]);

    const UpdateNewPassword = React.useCallback(async () => {
        setLoading(true);

        try {
            if (valid) {
                const response = await resetPassword({
                    email: values.email,
                    password: values.newPassword,
                    passwordConfirmation: values.newPasswordConfirmation,
                    token: values.token
                });

                if (response?.success) {
                    showSuccessToast({ message: `Your password has been successfully created.` });

                    navigate(AppRoutes.SIGNIN);
                }
                else {
                    showErrorToast({ message: response?.message });
                }
            }
        }
        catch (error) {
            console.log('Sign up with email', error);
        }

        setLoading(false);
    }, [values, valid]);

    const updateValid = React.useCallback((valuesNew: any) => {
        const validNew = !!(
            isValid.email(valuesNew.email)
        );

        setValid(validNew);
    }, []);

    const updateValue = React.useCallback((property: string, valueNew: any) => {
        setValues((previous: any) => {
            const valuesNew = {
                ...previous,
                [property]: valueNew
            };

            // Valid 
            updateValid(valuesNew);

            return valuesNew;
        });
    }, []);

    const updateVerificationDigits = React.useCallback((nextDigits: string[]) => {
        setVerificationDigits(nextDigits);
        updateValue('verificationCode', nextDigits.join(''));
    }, [updateValue]);

    const onVerificationDigitChange = React.useCallback((index: number, rawValue: string) => {
        const digit = rawValue.slice(-1);
        const nextDigits = [...verificationDigits];
        nextDigits[index] = digit;
        updateVerificationDigits(nextDigits);

        if (digit && index < nextDigits.length - 1) {
            verificationInputRefs.current[index + 1]?.focus();
        }
    }, [verificationDigits, updateVerificationDigits]);

    const onVerificationDigitKeyDown = React.useCallback((index: number, event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Backspace' && !verificationDigits[index] && index > 0) {
            verificationInputRefs.current[index - 1]?.focus();
        }
    }, [verificationDigits]);

    const onVerificationCodePaste = React.useCallback((event: React.ClipboardEvent<HTMLInputElement>) => {
        event.preventDefault();
        const pastedDigits = event.clipboardData.getData('text').slice(0, 8).split('');
        if (!pastedDigits.length) return;

        const nextDigits = Array(8).fill('').map((_, index) => pastedDigits[index] || '');
        updateVerificationDigits(nextDigits);
        verificationInputRefs.current[Math.min(pastedDigits.length, 8) - 1]?.focus();
    }, [updateVerificationDigits]);

    const parts = {
        form: (
            <InnerContainer>
                <Column
                    style={{
                        gap: 28
                    }}
                >
                    <FullLogo />
                    <HeaderBlock>
                        <Title>Reset your password</Title>
                        <Subtitle>Enter your email and we&apos;ll send you a verification code.</Subtitle>
                    </HeaderBlock>
                    <FormCard>
                        <FormCardBody>
                            <Label>
                                Email address
                                <AuthInputWrap>
                                    <Input
                                        placeholder='Enter your email address'
                                        type='email'
                                        value={values.email || ''}
                                        onChangeHandler={valueNew => updateValue('email', valueNew)}
                                    />
                                </AuthInputWrap>
                            </Label>
                            <AuthMethodButton
                                type='submit'
                                $type='light'
                                tooltipMessage=''
                                disabled={!valid}
                                onlySpinner={loading}
                                onClickHandler={SendResetInstructions}
                            >
                                Send verification code
                            </AuthMethodButton>
                            <FooterHint>
                                <BackToLoginLink onClick={() => navigate(AppRoutes.SIGNIN)}>Back to login</BackToLoginLink>
                            </FooterHint>
                            <Meta>
                                For security reasons, we do NOT store your password. We will never send your password via email.
                            </Meta>
                        </FormCardBody>
                    </FormCard>
                </Column>
            </InnerContainer>
        ),
        formVerficationConfirm: (
            <InnerContainer>
                <Column
                    style={{
                        gap: 28
                    }}
                >
                    <FullLogo />
                    <HeaderBlock>
                        <Title>Enter verification code</Title>
                        <Subtitle>
                            We sent a verification code to <StrongValue>{values.email}</StrongValue>
                        </Subtitle>
                    </HeaderBlock>
                    <VerificationCard>
                        <VerificationCardBody>
                            <CodeInputGroup>
                                <CodeInputsRow>
                                    {verificationDigits.map((digit, index) => (
                                        <CodeInput
                                            key={index}
                                            ref={element => {
                                                verificationInputRefs.current[index] = element;
                                            }}
                                            value={digit}
                                            onChange={event => onVerificationDigitChange(index, event.target.value)}
                                            onKeyDown={event => onVerificationDigitKeyDown(index, event)}
                                            onPaste={onVerificationCodePaste}
                                            inputMode="text"
                                            maxLength={1}
                                            autoComplete="one-time-code"
                                            aria-label={`Verification digit ${index + 1}`}
                                        />
                                    ))}
                                </CodeInputsRow>
                            </CodeInputGroup>
                            <AuthMethodButton
                                type='submit'
                                $type='light'
                                tooltipMessage=''
                                onlySpinner={loading}
                                onClickHandler={VerificationCodeConfirm}
                                disabled={values.verificationCode?.length !== 8 || loading}
                            >
                                Verify code
                            </AuthMethodButton>
                            <FooterHint>
                                <BackToLoginLink onClick={() => void resendVerificationCode()}>
                                    Resend verification link
                                </BackToLoginLink>
                            </FooterHint>
                            <FooterHint>
                                Remembered your password? Go back to{' '}
                                <LoginAccentLink onClick={() => navigate(AppRoutes.SIGNIN)}>login</LoginAccentLink>
                            </FooterHint>
                            <Meta>
                                For security reasons, we do NOT store your password. We will never send your password via email.
                            </Meta>
                        </VerificationCardBody>
                    </VerificationCard>
                </Column>
            </InnerContainer>
        ),
        updateNewPassword: (
            <InnerContainer>
            <Column
                style={{
                    gap: 28
                }}
            >
                <FullLogo />
                <HeaderBlock>
                    <Title>Create a new password</Title>
                </HeaderBlock>
                <FormCard>
                    <FormCardBody>
                        <Label>
                            <span>Password</span>
                            <AuthInputWrap>
                                <Input
                                    placeholder='Enter new password'
                                    type={showPassword === 'newPassword' ? 'text' : 'password'}
                                    value={values.newPassword || ''}
                                    onChangeHandler={valueNew => updateValue('newPassword', valueNew)}
                                    style={{
                                        paddingRight: 60
                                    }}
                                />
                            </AuthInputWrap>
                            <Show
                                onClick={() => setShowPassword((previous: any) => previous === 'newPassword' ? false : 'newPassword')}
                            >
                                {showPassword === 'newPassword' ? 'Hide' : 'Show'}
                            </Show>
                        </Label>
                        <Label>
                            <span>Password confirmation</span>
                            <AuthInputWrap>
                                <Input
                                    placeholder='Confirm new password'
                                    type={showPassword === 'newPasswordConfirmation' ? 'text' : 'password'}
                                    value={values.newPasswordConfirmation || ''}
                                    onChangeHandler={valueNew => updateValue('newPasswordConfirmation', valueNew)}
                                    style={{
                                        paddingRight: 60
                                    }}
                                />
                            </AuthInputWrap>
                            <Show
                                onClick={() => setShowPassword((previous: any) => previous === 'newPasswordConfirmation' ? false : 'newPasswordConfirmation')}
                            >
                                {showPassword === 'newPasswordConfirmation' ? 'Hide' : 'Show'}
                            </Show>
                        </Label>
                        <AuthMethodButton
                            type='submit'
                            $type='light'
                            tooltipMessage=''
                            onlySpinner={loading}
                            onClickHandler={UpdateNewPassword}
                            disabled={!(
                                values.newPassword &&
                                values.newPasswordConfirmation &&
                                values.newPassword === values.newPasswordConfirmation
                            ) || loading}
                        >
                            Reset password
                        </AuthMethodButton>
                        <Meta>
                            For security reasons, we do NOT store your password. We will never send your password via email.
                        </Meta>
                    </FormCardBody>
                </FormCard>
                </Column>
            </InnerContainer>
        )
    }

    return (
        <Container>
            {loaded && <>
                {step === 1 && parts.form}

                {step === 2 && parts.formVerficationConfirm}

                {step === 3 && parts.updateNewPassword}
            </>}
            <AuthLegalFooter />
        </Container>
    );
});

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  min-height: 100vh;
  background-color: white;
  padding: 40px max(24px, calc((100% - 500px) / 2)) 0;
`;

const InnerContainer = styled.div`
  display: flex;
  flex-direction: column;
  max-width: 500px;
  width: 100%;
  background-color: white;
`;

const Column = styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    width: 100%;
`;

const Row = styled.div`
    display: flex;
    flex-direction: row;
    gap: 4px;
`;

const Link = styled.span<{ color?: string }>`
    font-weight: bold;
    text-decoration: none;
    cursor: pointer;
    ${({ color }) => color ? `color: ${color};` : `color: ${styles.colors.primary600};`}
    transition: opacity 0.3s ease 0s; 

    &:hover {
        opacity: 0.54;
    }
`;

const Title = styled.h1`
    font-family: NunitoSans;
    font-style: normal;
    font-weight: 700;
    font-size: 32px;
    line-height: 32px;
    color: ${styles.colors.black600};
    margin: 0;
`;

const Subtitle = styled.p`
    font-family: NunitoSans;
    font-style: normal;
    font-weight: 400;
    font-size: 16px;
    line-height: 24px;
    color: #45556c;
    margin: 0;
    width: 100%;
`;

const StrongValue = styled.span`
    font-weight: 700;
    color: ${styles.colors.black600};
`;

const HeaderBlock = styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
    width: 100%;
`;

const FormCard = styled.div`
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 100%;
    border: 1px solid ${styles.colors.black200};
    border-radius: 12px;
    padding: 24px;
`;

const FormCardBody = styled.div`
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 16px;
`;

const VerificationCard = styled(FormCard)`
    padding: 32px 24px 24px;
`;

const VerificationCardBody = styled(FormCardBody)`
    gap: 24px;
`;

const CodeInputGroup = styled.div`
    display: flex;
    flex-direction: column;
    align-items: stretch;
    width: 100%;
    gap: 24px;
`;

const CodeInputsRow = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: flex-start;
    gap: 10px;
    width: 100%;
`;

const CodeInput = styled.input`
    box-sizing: border-box;
    width: 50px;
    height: 60px;
    border: 1px solid ${styles.colors.black200};
    border-radius: 8px;
    background: ${styles.colors.white};
    font-family: NunitoSans;
    font-weight: 700;
    font-size: 24px;
    line-height: 32px;
    text-align: center;
    color: ${styles.colors.black600};
    outline: none;

    &:focus {
        border-color: ${styles.colors.primary600};
    }
`;

const Label = styled.label`
    position: relative;
    display: flex;
    flex-direction: column;
    flex: 1 1 auto;
    gap: 6px; 
    width: 100%;
    font-family: NunitoSans;
    letter-spacing: 0.5px;
    font-size: 14px;
    line-height: 20px;
    text-transform: capitalize;
    font-weight: bold;
    color: ${(props: any) => props.theme.nonaryColor}; 
    cursor: default;
`;

const Show = styled.span`
    position: absolute;
    bottom: 8px;
    right: 12px;
    font-family: NunitoSans;
    font-style: normal;
    font-weight: 700;
    font-size: 11px;
    line-height: 16px;
    letter-spacing: 0.5px;
    text-transform: uppercase;
    color: ${(props: any) => props.theme.primary600};
    cursor: pointer;
    user-select: none; 
`
const Meta = styled.p`
    font-family: NunitoSans;
    font-style: normal;
    font-weight: 400;
    font-size: 14px;
    line-height: 18px;
    color: ${(props: any) => props.theme.nonaryColor}; 
    margin-top: 4px; 
    text-align: center;
`

const FooterHint = styled.p`
    margin: 0;
    width: 100%;
    font-family: NunitoSans;
    font-weight: 700;
    font-size: 14px;
    line-height: 20px;
    text-align: center;
    color: ${styles.colors.black400};
`;

const BackToLoginLink = styled.span`
    color: ${styles.colors.black400};
    font-family: NunitoSans;
    font-weight: 700;
    font-size: 16px;
    line-height: 24px;
    cursor: pointer;
    transition: opacity 0.2s ease;

    &:hover {
        opacity: 0.7;
    }
`;

const LoginAccentLink = styled(BackToLoginLink)`
    color: ${styles.colors.primary600};
`;
export default ForgotPasswordSection; 
